import { useCallback, useEffect, useMemo, useRef, useState } from "react";

import Link from "next/link";
import { useRouter } from "next/navigation";

import { AnimatePresence, motion } from "framer-motion";
import { Bot, ChevronLeft, ExternalLink } from "lucide-react";
import { toast } from "sonner";

import { DebugOverlay } from "@/components/debug/DebugOverlay";
import { ColorSelection } from "@/components/product/ColorSelection";
import ImageCarousel from "@/components/product/ImageCarousel";
import { PdpActionIcons } from "@/components/product/PdpActionIcons";
import { getDesktopPdpHeroLayoutMode } from "@/components/product/productDetailPageImageLayout";
import ProductImage from "@/components/product/ProductImage";
import { RelatedProductsRail } from "@/components/product/RelatedProductsRail";
import { SellerAttribution } from "@/components/product/SellerAttribution";
import { Button } from "@/components/ui/button";
import { ExpandableSection } from "@/components/ui/ExpandableSection";
import { MoreLikeThisModal, type MoreLikeThisSearchData } from "@/components/ui/MoreLikeThisModal";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { useCart } from "@/contexts/CartContext";
import { useDebug } from "@/contexts/DebugContext";
import { useSavedProducts } from "@/contexts/SavedProductsContext";
import { useDesktopImageGallery } from "@/hooks/useDesktopImageGallery";
import { useOutboundRedirectUrl } from "@/hooks/useOutboundRedirectUrl";
import { useProductImageMetadata } from "@/hooks/useProductImageMetadata";
import { useSimilarProducts } from "@/hooks/useSimilarProducts";
import { buildCanonicalBrandPath, isBrandPageAvailable } from "@/lib/brand-routing";
import { hasCompleteColorSwatchData } from "@/lib/color-swatch-utils";
import { dispatchMltSearch } from "@/lib/mlt-utils";
import {
  buildCanonicalProductPathFromRecord,
  getProductBrandSlugFromRecord,
} from "@/lib/product-url";
import {
  type Color,
  type Product,
  areVariantsSelected,
  buildVariantSpec,
  getDefaultSelectedColor,
  getDefaultSelectedFit,
  getDefaultSelectedPattern,
  getDefaultSelectedSize,
  getFormattedPrice,
  getProductAnyId,
  getProductAvailabilityStatus,
  getProductCatalogDisplayName,
  getProductImageSources,
  getProductName,
  getProductPrimaryActionLabels,
  getSavedProductId,
} from "@/lib/product-utils";
import { escapeHtml, sanitizeProductDescription } from "@/lib/sanitize-html";
import { shareProductPageLink } from "@/lib/share";
import { colors } from "@/styles/design-tokens";

interface ProductDetailPageProps {
  product: Product | null;
  onBackToShopping?: () => void;
  onAction?: (action: string) => void;
  preferEscapedDescriptionUntilHydrated?: boolean;
}

export function ProductDetailPage({
  product,
  onBackToShopping,
  onAction,
  preferEscapedDescriptionUntilHydrated = false,
}: ProductDetailPageProps) {
  // Variant selection
  const [selectedSize, setSelectedSize] = useState<string>("");
  const [selectedColor, setSelectedColor] = useState<Color | null>(null);
  const [selectedPattern, setSelectedPattern] = useState<string | null>(null);
  const [selectedFit, setSelectedFit] = useState<string | null>(null);
  const [sizeError, setSizeError] = useState(false);

  // UI state
  const [showShareTooltip, setShowShareTooltip] = useState(false);
  const [showMoreLikeModal, setShowMoreLikeModal] = useState(false);

  // Measured image dimensions for desktop layout (fallback when schema dims are missing)
  const [measuredImageDims, setMeasuredImageDims] = useState<
    Record<number, { width: number; height: number }>
  >({});

  const handleDesktopImageLoad = useCallback(
    (imageIndex: number, img: { naturalWidth: number; naturalHeight: number }) => {
      setMeasuredImageDims((prev) => {
        if (prev[imageIndex]) return prev;
        return { ...prev, [imageIndex]: { width: img.naturalWidth, height: img.naturalHeight } };
      });
    },
    []
  );

  const router = useRouter();

  // Auth and cart management
  const { addItem, isInCart, loading: cartLoading } = useCart();
  const { isSaved } = useSavedProducts();
  const { isDebug } = useDebug();

  // Image metadata
  const { handleImageLoad, objectFit } = useProductImageMetadata(product);

  // Product images (sources carry width/height from the schema for aspect-ratio sizing)
  const allImageSources = useMemo(
    () => (product ? getProductImageSources(product) : []),
    [product]
  );
  const allImages = useMemo(() => allImageSources.map((s) => s.primary), [allImageSources]);
  const secondaryImageSources = useMemo(() => allImageSources.slice(1), [allImageSources]);
  const hasSecondaryImages = secondaryImageSources.length > 0;

  // Resolve face image dimensions: schema first, then measured on load
  const faceDims = useMemo(() => {
    const s = allImageSources[0];
    if (s?.width && s?.height) return { width: s.width, height: s.height };
    return measuredImageDims[0] ?? null;
  }, [allImageSources, measuredImageDims]);

  const firstSecondaryDims = useMemo(() => {
    const firstSecondarySource = secondaryImageSources[0];
    if (!firstSecondarySource) return null;
    if (firstSecondarySource.width && firstSecondarySource.height) {
      return { width: firstSecondarySource.width, height: firstSecondarySource.height };
    }
    // secondaryImageSources is allImageSources.slice(1), so the first secondary
    // image still maps to overall image index 1 for measured-dimension fallback.
    return measuredImageDims[1] ?? null;
  }, [secondaryImageSources, measuredImageDims]);

  const desktopHeroLayoutMode = useMemo(
    () =>
      getDesktopPdpHeroLayoutMode({
        hasSecondaryImages,
        faceDims,
        firstSecondaryDims,
      }),
    [hasSecondaryImages, faceDims, firstSecondaryDims]
  );

  const desktopHeroIsCenteredWide = desktopHeroLayoutMode === "centered-wide";
  const desktopHeroStickyStyle = useMemo(() => {
    if (desktopHeroIsCenteredWide) return undefined;
    if (hasSecondaryImages && faceDims) {
      return {
        maxHeight: "100svh",
        aspectRatio: `${faceDims.width} / ${faceDims.height}`,
      };
    }
    if (hasSecondaryImages) {
      return { height: "100svh" };
    }
    return undefined;
  }, [desktopHeroIsCenteredWide, hasSecondaryImages, faceDims]);

  const desktopHeroObjectFit = desktopHeroIsCenteredWide
    ? "contain"
    : hasSecondaryImages && faceDims
      ? "cover"
      : objectFit;

  const productColors = useMemo(
    () => product?.color_info?.colors ?? [],
    [product?.color_info?.colors]
  );

  // Desktop image gallery (IntersectionObserver-based counter)
  const desktopImagesRef = useRef<HTMLDivElement>(null);
  const imagesSentinelRef = useRef<HTMLDivElement>(null);
  const currentProductId = product ? getProductAnyId(product) : null;

  const { visibleImageIndex, showImageCounter, resetGallery } = useDesktopImageGallery({
    allImagesCount: allImages.length,
    currentProductId,
    desktopImagesRef,
    imagesSentinelRef,
  });

  // Similar / related products: the search runs only once the rail's position
  // nears the viewport (see useSimilarProducts), so page loads that never
  // scroll, headless renderers included, do not issue it.
  const relatedProductsSentinelRef = useRef<HTMLDivElement>(null);
  const { relatedProducts, loadingRelated } = useSimilarProducts(product, {
    sentinelRef: relatedProductsSentinelRef,
  });

  // SSR-safe description sanitization
  const [hasHydrated, setHasHydrated] = useState(false);
  const rawDescription = product?.description ?? "";
  const escapedDescription = useMemo(() => escapeHtml(rawDescription), [rawDescription]);
  const sanitizedDescription = useMemo(() => {
    if (!rawDescription) return "";
    if (!preferEscapedDescriptionUntilHydrated) {
      return sanitizeProductDescription(rawDescription);
    }
    // During SSR and the initial hydration pass, return escaped content so markup
    // is identical. After hydration, use DOMPurify to preserve safe HTML tags.
    return hasHydrated ? sanitizeProductDescription(rawDescription) : escapedDescription;
  }, [rawDescription, escapedDescription, hasHydrated, preferEscapedDescriptionUntilHydrated]);

  useEffect(() => {
    setHasHydrated(true);
  }, []);

  // Initialize default selections when product changes
  useEffect(() => {
    if (product) {
      setSelectedColor(getDefaultSelectedColor(product));
      setSelectedSize(getDefaultSelectedSize(product) || "");
      setSelectedPattern(getDefaultSelectedPattern(product));
      setSelectedFit(getDefaultSelectedFit(product));
      resetGallery();
      setMeasuredImageDims({});
    }
  }, [product, resetGallery]);

  // Derived values
  const productId = product ? getProductAnyId(product) : "unknown";
  const destinationUrl = product?.url ?? null;
  const retailerName = useMemo(() => {
    if (!product?.url) return "RETAILER";
    try {
      const parts = new URL(product.url).hostname.split(".");
      return parts.length >= 2 ? parts[parts.length - 2].toUpperCase() : "RETAILER";
    } catch {
      return "RETAILER";
    }
  }, [product?.url]);

  const { redirectUrl, isLoading: isRedirectUrlLoading } = useOutboundRedirectUrl(destinationUrl, {
    resourceType: "product",
    resourceId: productId,
    merchant: retailerName,
    catalog: product?.catalog ?? null,
  });

  const brandName = useMemo((): string => {
    if (product?.enrichment?.canonical_brand?.brand_name) {
      return product.enrichment.canonical_brand.brand_name;
    }
    if (product?.brand_name) return product.brand_name;
    if (product?.catalog)
      return product.catalog.charAt(0).toUpperCase() + product.catalog.slice(1).toLowerCase();
    return "Brand";
  }, [product]);
  const catalogDisplayName = useMemo(() => getProductCatalogDisplayName(product), [product]);
  const sellerDisplayName = catalogDisplayName || retailerName;
  const availabilityStatus = useMemo(() => getProductAvailabilityStatus(product), [product]);
  const primaryActionLabel = useMemo(
    () =>
      getProductPrimaryActionLabels({
        catalogDisplayName,
        sellerDisplayName,
        isOutOfStock: availabilityStatus.isOutOfStock,
      }),
    [availabilityStatus.isOutOfStock, catalogDisplayName, sellerDisplayName]
  );

  // Markdown/LLM-friendly version of the PDP for AI agents. The path is the
  // canonical product path with a `.md` suffix (see next.config.ts rewrites).
  const markdownPath = useMemo(() => {
    if (!product) return null;
    const canonicalPath = buildCanonicalProductPathFromRecord(
      product as unknown as Record<string, unknown>,
      productId
    );
    return canonicalPath ? `${canonicalPath}.md` : null;
  }, [product, productId]);

  if (!product) {
    return (
      <div className="flex min-h-screen items-center justify-center">
        <div className="text-center">
          <h2 className="mb-2 text-xl font-medium text-gray-900">Product not found</h2>
          {onBackToShopping && <Button onClick={onBackToShopping}>Back to Shopping</Button>}
        </div>
      </div>
    );
  }

  const savedProductId = getSavedProductId(product, isSaved);
  const inCart = isInCart(productId);
  const allowMissingColor = true;

  const variantsSelected = areVariantsSelected(
    product,
    {
      color: selectedColor,
      size: selectedSize,
      pattern: selectedPattern,
      fit: selectedFit,
    },
    { allowMissingSize: true, allowMissingColor }
  );
  const missingColor =
    !allowMissingColor && (product.color_info?.colors?.length || 0) > 0 && !selectedColor;

  const handleAddToBag = async () => {
    if (!variantsSelected) {
      setSizeError(true);
      return;
    }
    setSizeError(false);

    try {
      const variantSpec = buildVariantSpec({
        color: selectedColor,
        size: selectedSize,
        pattern: selectedPattern,
        fit: selectedFit,
      });
      await addItem(productId, variantSpec);
      toast.success(`Added ${getProductName(product)} to bag!`);
      onAction?.("add_to_cart");
    } catch (err) {
      console.error("Failed to add item to cart", err);
      toast.error("Failed to add item to bag");
    }
  };

  const handleBuyNow = ({
    skipVariantValidation = false,
  }: {
    skipVariantValidation?: boolean;
  } = {}) => {
    if (!skipVariantValidation && !variantsSelected) {
      setSizeError(true);
      return;
    }
    setSizeError(false);

    if (isRedirectUrlLoading) {
      return;
    }

    const outboundUrl = redirectUrl || destinationUrl;
    if (!outboundUrl) {
      toast.error("Retailer link unavailable");
      return;
    }
    const openedWindow = window.open(outboundUrl, "_blank", "noopener,noreferrer");
    if (!openedWindow) {
      toast.error("Couldn't open retailer site. Please allow pop-ups and try again.");
      return;
    }
    onAction?.("buy_now");
  };

  const handleImageError = () => {
    console.warn(`Image failed to load for product: ${getProductName(product)}`);
  };

  const handleShare = async () => {
    try {
      const outcome = await shareProductPageLink({
        productId,
        title: getProductName(product),
        product: product as unknown as Record<string, unknown>,
      });
      if (outcome === "copied") toast.success("Link copied");
      else if (outcome === "shared") toast.success("Shared");
      else if (outcome === "manual") toast.success("Copy link");
    } catch (err) {
      console.error("Failed to share product link", err);
      toast.error("Couldn't share link");
    }
  };

  const handleMoreLikeSubmit = (searchData: MoreLikeThisSearchData) => {
    setShowMoreLikeModal(false);
    const { navigateTo } = dispatchMltSearch(searchData);
    if (navigateTo) router.push(navigateTo);
  };

  const getBrandPath = (): string =>
    buildCanonicalBrandPath({
      brandName,
      brandSlug: getProductBrandSlugFromRecord(product as Record<string, unknown>),
    });
  // Brands that exist only in the product index have no brand page; render
  // the name as text instead of a link that would 404.
  const brandPageAvailable = isBrandPageAvailable(product as Record<string, unknown>);

  const price = getFormattedPrice(product);
  const showColorSwatches = hasCompleteColorSwatchData(productColors);
  const hasBuyNowDestination = Boolean(destinationUrl) && !isRedirectUrlLoading;

  const aiAgentMarkdownLink = markdownPath ? (
    <a
      href={markdownPath}
      className="inline-flex items-center gap-1.5 text-xs text-gray-500 transition-colors hover:text-gray-900 hover:underline"
      data-testid="pdp-ai-agent-markdown-link"
    >
      <Bot aria-hidden="true" className="h-3.5 w-3.5" />
      <span>If you&rsquo;re an AI agent, see this link for structured info</span>
    </a>
  ) : null;

  return (
    <>
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.6 }}
        className="min-h-screen bg-white"
      >
        {/* Desktop Layout */}
        <div className="hidden lg:block" ref={desktopImagesRef}>
          <div
            className={`grid ${hasSecondaryImages ? "grid-cols-[minmax(0,1fr)_minmax(0,1fr)_25%]" : "grid-cols-[1fr_25%]"}`}
          >
            {/* Face Image — sticky, sized by image aspect-ratio (capped at viewport height) */}
            <div
              className={
                desktopHeroIsCenteredWide
                  ? "sticky top-0 h-[100svh]"
                  : `sticky top-0 ${hasSecondaryImages ? "" : "h-[100svh] pb-4 pt-8"}`
              }
              style={desktopHeroStickyStyle}
              data-pdp-desktop-hero-layout={desktopHeroLayoutMode}
            >
              <div
                className={
                  desktopHeroIsCenteredWide
                    ? "flex h-full items-center bg-white"
                    : `relative bg-white ${hasSecondaryImages ? "h-full" : "h-[calc(100svh-3rem)]"}`
                }
                data-testid="pdp-image"
              >
                <div
                  className={desktopHeroIsCenteredWide ? "relative w-full" : "contents"}
                  style={
                    desktopHeroIsCenteredWide && faceDims
                      ? { aspectRatio: `${faceDims.width} / ${faceDims.height}` }
                      : undefined
                  }
                >
                  <ProductImage
                    src={allImages[0]}
                    alt={getProductName(product)}
                    objectFit={desktopHeroObjectFit}
                    onLoad={(img) => {
                      handleImageLoad(img);
                      handleDesktopImageLoad(0, img);
                    }}
                    onError={handleImageError}
                    priority
                    loading="eager"
                  />
                </div>
              </div>
            </div>

            {/* Scrolling Images — stacked vertically, sized by aspect-ratio */}
            {hasSecondaryImages && (
              <div>
                {secondaryImageSources.map((source, index) => {
                  const dims =
                    source.width && source.height
                      ? { width: source.width, height: source.height }
                      : measuredImageDims[index + 1];
                  return (
                    <div
                      key={`${source.primary}-${index}`}
                      data-secondary-image-index={index + 1}
                      className="relative bg-white"
                      style={
                        dims
                          ? {
                              aspectRatio: `${dims.width} / ${dims.height}`,
                              maxHeight: "100svh",
                            }
                          : { height: "100svh" }
                      }
                    >
                      <ProductImage
                        src={source.primary}
                        fallbackSrc={source.fallback}
                        alt={`${getProductName(product)} — Image ${index + 2}`}
                        objectFit={dims ? "cover" : "contain"}
                        onLoad={(img) => handleDesktopImageLoad(index + 1, img)}
                        loading={index === 0 ? "eager" : "lazy"}
                      />
                    </div>
                  );
                })}

                {/* Sentinel for hiding the counter after image column content. */}
                <div ref={imagesSentinelRef} className="h-px" aria-hidden="true" />
              </div>
            )}

            {/* Product Details — sticky sidebar (native scrollbar intentionally visible) */}
            <div className="sticky top-0 flex h-[100svh] flex-col overflow-y-auto px-8 pt-8">
              {/* Header */}
              <div className="mb-8 space-y-5">
                {onBackToShopping && (
                  <button
                    onClick={onBackToShopping}
                    className="group inline-flex w-fit items-center gap-2 text-sm text-gray-500 transition-colors duration-200 hover:text-gray-900"
                    aria-label="Back to shopping"
                    data-testid="pdp-back-button"
                  >
                    <ChevronLeft className="h-4 w-4 transition-transform duration-200 group-hover:-translate-x-0.5" />
                    <span>Back</span>
                  </button>
                )}

                {/* Brand with Actions */}
                <div className="flex items-center justify-between">
                  <p className="text-xs font-normal uppercase tracking-[0.2em] text-gray-500">
                    {brandPageAvailable ? (
                      <Link href={getBrandPath()} className="hover:underline">
                        {brandName}
                      </Link>
                    ) : (
                      <span data-testid="pdp-brand-label">{brandName}</span>
                    )}
                  </p>

                  <PdpActionIcons
                    product={product}
                    savedProductId={savedProductId}
                    onOpenMoreLike={() => setShowMoreLikeModal(true)}
                    onShare={handleShare}
                    className="flex flex-shrink-0 items-center gap-1"
                    shareIconClassName="h-[18px] w-[18px] stroke-[1.5]"
                    showShareTooltip={showShareTooltip}
                    onShareTooltipChange={setShowShareTooltip}
                  />
                </div>

                {/* Product Name + Price */}
                <div className="flex items-start justify-between gap-4">
                  <h1
                    className="text-xl font-medium leading-tight text-gray-900"
                    data-testid="pdp-name"
                  >
                    {getProductName(product)}
                  </h1>
                  <div className="flex shrink-0 flex-col items-end">
                    <span className="text-lg font-semibold text-gray-900" data-testid="pdp-price">
                      {price.final}
                    </span>
                    {price.original && (
                      <span className="text-sm font-normal text-gray-400 line-through">
                        {price.original}
                      </span>
                    )}
                  </div>
                </div>

                <SellerAttribution
                  productId={productId}
                  productUrl={product.url}
                  redirectUrl={redirectUrl}
                  destinationUrl={destinationUrl}
                  isRedirectLoading={isRedirectUrlLoading}
                  retailerName={retailerName}
                  sellerDisplayName={sellerDisplayName}
                />
                {availabilityStatus.statusLabel && (
                  <p
                    className="text-sm font-medium text-amber-700"
                    data-testid="pdp-availability-status"
                  >
                    {availabilityStatus.statusLabel}
                  </p>
                )}
              </div>

              {/* Color Selection */}
              <ColorSelection
                colors={productColors}
                selectedColor={selectedColor}
                showColorSwatches={showColorSwatches}
                showValidationError={sizeError}
                missingColor={missingColor}
                headingClassName="mb-3 text-xs font-medium uppercase tracking-[0.2em] text-gray-900"
                wrapperClassName="mb-6"
                onSelectColor={setSelectedColor}
              />

              {/* Size Selection */}
              {product.sizes && product.sizes.length > 0 && (
                <div className="mb-6">
                  <h3 className="mb-3 text-xs font-medium uppercase tracking-[0.2em] text-gray-900">
                    Size
                  </h3>
                  <Select
                    value={selectedSize || undefined}
                    onValueChange={(value) => {
                      setSelectedSize(value);
                      setSizeError(false);
                    }}
                  >
                    <SelectTrigger className="h-12 rounded-none border-gray-300 bg-white px-4 text-base text-gray-900 data-[placeholder]:text-gray-500 focus-visible:ring-0">
                      <SelectValue placeholder="Select size" />
                    </SelectTrigger>
                    <SelectContent>
                      {product.sizes.map((size: string) => (
                        <SelectItem key={size} value={size}>
                          {size}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              )}

              {/* Action Buttons */}
              <div className="mb-8 space-y-3">
                <Button
                  onClick={() => {
                    if (!availabilityStatus.isOutOfStock && !variantsSelected) {
                      setSizeError(true);
                      return;
                    }
                    handleBuyNow({ skipVariantValidation: availabilityStatus.isOutOfStock });
                  }}
                  aria-label={primaryActionLabel.accessibleLabel}
                  className="w-full min-w-0 overflow-hidden border-0 bg-gray-900 py-3 text-sm font-medium text-white shadow-none hover:bg-gray-800"
                  disabled={!hasBuyNowDestination}
                >
                  <span className="min-w-0 truncate">{primaryActionLabel.visibleLabel}</span>
                  <ExternalLink aria-hidden="true" className="h-4 w-4 flex-shrink-0" />
                  <span className="sr-only">(opens in new tab)</span>
                </Button>
                <Button
                  onClick={() => {
                    if (!variantsSelected) {
                      setSizeError(true);
                      return;
                    }
                    handleAddToBag();
                  }}
                  variant="outline"
                  className="w-full bg-white py-3 text-sm font-medium"
                  style={{
                    borderColor: colors.text.black,
                    color: colors.text.black,
                  }}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.borderColor = colors.text.black;
                    e.currentTarget.style.backgroundColor = colors.accent.veryLightGray;
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.borderColor = colors.text.black;
                    e.currentTarget.style.backgroundColor = "white";
                  }}
                  disabled={cartLoading || inCart}
                  data-testid="pdp-add-to-bag"
                >
                  {inCart ? "Added to Bag" : "Add to Bag"}
                </Button>
              </div>

              {/* Expandable Sections */}
              <div className="space-y-0 border-t border-gray-100">
                {product.materials && product.materials.length > 0 && (
                  <ExpandableSection title="Materials & Care">
                    <div className="space-y-4">
                      <div>
                        <h4 className="mb-2 text-xs font-medium uppercase tracking-[0.2em] text-gray-900">
                          Materials
                        </h4>
                        <p className="text-gray-600">{product.materials.join(", ")}</p>
                      </div>
                    </div>
                  </ExpandableSection>
                )}

                {(product.fit || product.dimensions) && (
                  <ExpandableSection title="Size & Fit">
                    <div className="space-y-2">
                      <p className="text-gray-600">True to size</p>
                      {product.fit && Array.isArray(product.fit) && (
                        <p className="text-gray-600">{product.fit.join(", ")}</p>
                      )}
                      {product.fit && typeof product.fit === "string" && (
                        <p className="text-gray-600">{product.fit}</p>
                      )}
                    </div>
                  </ExpandableSection>
                )}
              </div>

              {/* Description */}
              {product.description && (
                <div className="mt-6">
                  <div
                    className="text-sm leading-relaxed text-gray-600"
                    dangerouslySetInnerHTML={{ __html: sanitizedDescription }}
                  />
                </div>
              )}

              {/* AI-agent-friendly markdown link */}
              {aiAgentMarkdownLink && <div className="mt-6 pb-8">{aiAgentMarkdownLink}</div>}
            </div>
          </div>
        </div>

        {/* Mobile Layout */}
        <div className="lg:hidden">
          <div>
            {onBackToShopping && (
              <div className="px-6 pb-2 pt-4">
                <button
                  onClick={onBackToShopping}
                  className="group inline-flex w-fit items-center gap-2 text-sm text-gray-500 transition-colors duration-200 hover:text-gray-900"
                  aria-label="Back to shopping"
                  data-testid="pdp-back-button"
                >
                  <ChevronLeft className="h-4 w-4 transition-transform duration-200 group-hover:-translate-x-0.5" />
                  <span>Back</span>
                </button>
              </div>
            )}

            {/* Full-width Product Image */}
            <div className="relative">
              <div className="aspect-square overflow-hidden bg-white">
                <div data-testid="pdp-image">
                  <ImageCarousel
                    images={allImages}
                    productName={getProductName(product)}
                    objectFit={objectFit}
                    onImageLoad={handleImageLoad}
                    onImageError={handleImageError}
                    className="h-full w-full"
                  />
                </div>
              </div>
            </div>

            {/* Content - Stacked below */}
            <div className="space-y-8 px-6 pb-4 pt-8">
              {/* Header */}
              <div className="space-y-6">
                {/* Brand with Actions */}
                <div className="flex items-center justify-between">
                  <p className="text-xs font-normal uppercase tracking-[0.2em] text-gray-500">
                    {brandPageAvailable ? (
                      <Link href={getBrandPath()} className="hover:underline">
                        {brandName}
                      </Link>
                    ) : (
                      <span data-testid="pdp-brand-label">{brandName}</span>
                    )}
                  </p>

                  <PdpActionIcons
                    product={product}
                    savedProductId={savedProductId}
                    onOpenMoreLike={() => setShowMoreLikeModal(true)}
                    onShare={handleShare}
                    className="flex flex-shrink-0 items-center gap-2"
                    shareIconClassName="h-5 w-5 stroke-[1.5]"
                  />
                </div>

                {/* Product Name */}
                <h1
                  className="text-2xl font-medium leading-tight text-gray-900"
                  data-testid="pdp-name"
                >
                  {getProductName(product)}
                </h1>

                {/* Price */}
                <div className="flex items-baseline gap-3">
                  <span className="text-2xl font-semibold text-gray-900" data-testid="pdp-price">
                    {price.final}
                  </span>
                  {price.original && (
                    <span className="text-lg font-normal text-gray-400 line-through">
                      {price.original}
                    </span>
                  )}
                </div>

                <SellerAttribution
                  productId={productId}
                  productUrl={product.url}
                  redirectUrl={redirectUrl}
                  destinationUrl={destinationUrl}
                  isRedirectLoading={isRedirectUrlLoading}
                  retailerName={retailerName}
                  sellerDisplayName={sellerDisplayName}
                />
                {availabilityStatus.statusLabel && (
                  <p
                    className="text-sm font-medium text-amber-700"
                    data-testid="pdp-availability-status"
                  >
                    {availabilityStatus.statusLabel}
                  </p>
                )}
              </div>

              {/* Description */}
              {product.description && (
                <div>
                  <div
                    className="leading-relaxed text-gray-600"
                    dangerouslySetInnerHTML={{ __html: sanitizedDescription }}
                  />
                </div>
              )}

              {/* Color Selection */}
              <ColorSelection
                colors={productColors}
                selectedColor={selectedColor}
                showColorSwatches={showColorSwatches}
                showValidationError={sizeError}
                missingColor={missingColor}
                headingClassName="mb-4 text-xs font-medium uppercase tracking-[0.2em] text-gray-900"
                onSelectColor={setSelectedColor}
              />

              {/* Size Selection */}
              {product.sizes && product.sizes.length > 0 && (
                <div>
                  <h3 className="mb-4 text-xs font-medium uppercase tracking-[0.2em] text-gray-900">
                    Size
                  </h3>
                  <Select
                    value={selectedSize || undefined}
                    onValueChange={(value) => {
                      setSelectedSize(value);
                      setSizeError(false);
                    }}
                  >
                    <SelectTrigger className="h-12 rounded-none border-gray-300 bg-white px-4 text-base text-gray-900 data-[placeholder]:text-gray-500 focus-visible:ring-0">
                      <SelectValue placeholder="Select size" />
                    </SelectTrigger>
                    <SelectContent>
                      {product.sizes.map((size: string) => (
                        <SelectItem key={size} value={size}>
                          {size}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              )}

              {/* Action Buttons - Inline */}
              <div className="space-y-3">
                <Button
                  onClick={() => {
                    if (!availabilityStatus.isOutOfStock && !variantsSelected) {
                      setSizeError(true);
                      return;
                    }
                    handleBuyNow({ skipVariantValidation: availabilityStatus.isOutOfStock });
                  }}
                  aria-label={primaryActionLabel.accessibleLabel}
                  className="w-full min-w-0 overflow-hidden border-0 bg-gray-900 py-3 text-sm font-medium text-white shadow-none hover:bg-gray-800"
                  disabled={!hasBuyNowDestination}
                >
                  <span className="min-w-0 truncate">{primaryActionLabel.visibleLabel}</span>
                  <ExternalLink aria-hidden="true" className="h-4 w-4 flex-shrink-0" />
                  <span className="sr-only">(opens in new tab)</span>
                </Button>
                <Button
                  onClick={() => {
                    if (!variantsSelected) {
                      setSizeError(true);
                      return;
                    }
                    handleAddToBag();
                  }}
                  variant="outline"
                  className="w-full bg-white py-3 text-sm font-medium"
                  style={{
                    borderColor: colors.text.black,
                    color: colors.text.black,
                  }}
                  disabled={cartLoading || inCart}
                  data-testid="pdp-add-to-bag"
                >
                  {inCart ? "Added to Bag" : "Add to Bag"}
                </Button>
              </div>

              {/* Expandable Sections */}
              <div className="space-y-0 border-t border-gray-100">
                {product.materials && product.materials.length > 0 && (
                  <ExpandableSection title="Materials & Care">
                    <div className="space-y-4">
                      <div>
                        <h4 className="mb-2 text-xs font-medium uppercase tracking-[0.2em] text-gray-900">
                          Materials
                        </h4>
                        <p className="text-gray-600">{product.materials.join(", ")}</p>
                      </div>
                    </div>
                  </ExpandableSection>
                )}

                {(product.fit || product.dimensions) && (
                  <ExpandableSection title="Size & Fit">
                    <div className="space-y-2">
                      <p className="text-gray-600">True to size</p>
                      {product.fit && Array.isArray(product.fit) && (
                        <p className="text-gray-600">{product.fit.join(", ")}</p>
                      )}
                      {product.fit && typeof product.fit === "string" && (
                        <p className="text-gray-600">{product.fit}</p>
                      )}
                    </div>
                  </ExpandableSection>
                )}
              </div>

              {/* AI-agent-friendly markdown link */}
              {aiAgentMarkdownLink && <div>{aiAgentMarkdownLink}</div>}
            </div>
          </div>
        </div>

        {/* Related Products Rail. The sentinel always renders (the rail itself
            renders nothing until results arrive) so visibility can be observed. */}
        <div ref={relatedProductsSentinelRef} aria-hidden="true" className="h-px" />
        <RelatedProductsRail
          relatedProducts={relatedProducts}
          loadingRelated={loadingRelated}
          currentProductId={currentProductId}
        />

        {/* More Like This Modal */}
        {showMoreLikeModal && (
          <MoreLikeThisModal
            product={product}
            onClose={() => setShowMoreLikeModal(false)}
            onSubmit={handleMoreLikeSubmit}
          />
        )}

        {/* Footer spacer — prevents fixed buttons from occluding content */}
        <div className="h-4" aria-hidden="true" />
      </motion.div>

      {/* Keep counter outside transformed motion container so fixed positioning uses viewport. */}
      {allImages.length > 1 && !showMoreLikeModal && (
        <div
          className={`fixed bottom-6 left-6 z-[51] hidden text-sm tabular-nums text-gray-500 transition-opacity duration-300 lg:block lg:bottom-[5.5rem] xl:bottom-16 ${
            showImageCounter ? "opacity-100" : "pointer-events-none opacity-0"
          }`}
        >
          {visibleImageIndex + 1} / {allImages.length}
        </div>
      )}

      {/* Debug overlay toggled via Cmd+Shift+D */}
      <AnimatePresence>{isDebug && product && <DebugOverlay product={product} />}</AnimatePresence>
    </>
  );
}
